import React, { useState } from "react";
import { API, fileUpload } from "../../../../config";
import { toast } from "react-toastify";
import { Button, Form, FormControl, Modal } from "react-bootstrap";
import Spin from "../../../common/Spin";
import ContractorSelectWithTA from "../../../common/ContractorSelectWithTA";
import { useParams } from "react-router-dom";
import { ErrorMessage, Formik } from "formik";
import { getContractorDetail } from "./SubContractorHelper";
import NumberFormat from "react-number-format";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload, faXmarkCircle } from "@fortawesome/free-solid-svg-icons";
import { AddContractSchema } from "../../../common/Validation";

const AddContractModal = ({
  toggleAddModal,
  handleAdd,
  showAdd,
  contractor,
  setNewContract,
}) => {
  const params = useParams();
  const [loading, setLoading] = useState(false);
  const projectId = params?.project_id;
  // eslint-disable-next-line no-unused-vars
  const [contractorDetails, setContractorDetails] = useState(contractor);
  const [disableState, setDisableState] = useState(false);

  const [uploadedFiles, setUploadedFiles] = useState([]);

  const handleFiles = (files, setFieldValue, multiple) => {
    const allowedTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
      "image/jpeg",
      "image/jpg",
      "image/png",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/msword",
      "application/pdf",
    ];

    const validFiles = files.filter((file) => allowedTypes.includes(file.type));

    if (validFiles.length > 0 && multiple) {
      setUploadedFiles((prev) => [...prev, ...validFiles]);
      setFieldValue("documents", [...uploadedFiles, ...validFiles]);
    } else if (validFiles.length > 0) {
      setUploadedFiles([...validFiles]);
      setFieldValue("documents", [...validFiles]);
    } else {
      toast.error("Invalid file type.");
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const formData = new FormData();
      values.contact_id = values?.contact_id?.id;
      if (values.contract_value) {
        values.contract_value = Number(
          `${values.contract_value}`.replace(/,/g, "")
        );
      }

      if (values.contract_value_exc) {
        values.contract_value_exc = Number(
          `${values.contract_value_exc}`.replace(/,/g, "")
        );
      }

      if (values.retention_amount) {
        values.retention_amount = Number(
          `${values?.retention_amount}`.replace(/,/g, "")
        );
      } else {
        values.retention_amount = 0;
      }

      if (values.retention_percentage) {
        values.retention_percentage = Number(
          `${values?.retention_percentage}`?.replace("%", "")
        );
      } else {
        values.retention_percentage = 0;
      }

      for (const key in values) {
        if (key !== "documents") {
          if (key === "file_url") {
            if (values[key]) {
              let valueData = values[key].replace(/\s*,\s*/g, ",")?.split(",");
              for (
                let valueIndex = 0;
                valueIndex < valueData?.length;
                valueIndex++
              ) {
                formData.append(`${key}[${valueIndex}]`, valueData[valueIndex]);
              }
            }
          } else {
            formData.append(key, values[key]);
          }
        }
      }
      uploadedFiles.forEach((file, index) => {
        formData.append(`documents[${index}]`, file);
      });
      formData.append("builder_project_id", projectId);
      formData.append("document_type", 1);
      const { data } = await fileUpload(`${API.UPLOAD_DOCUMENT}`, formData);
      setLoading(false);
      setNewContract(data?.data);
      toast.success(data?.message);
      handleAdd();
      toggleAddModal();
    } catch (e) {
      setLoading(false);
      return null;
    }
  };

  const getContractor = async (contractorId) => {
    try {
      setLoading(true);
      const data = await getContractorDetail(contractorId);
      const contractorData = data ? data : {};
      if (contractorData?.id) {
        if (
          contractorData?.account_name &&
          contractorData?.account_number &&
          contractorData?.bsb
        ) {
          setDisableState(false);
        } else {
          setDisableState(true);
          toast.error("Please add bank details of selected contractor.");
        }
      }
      setLoading(false);
    } catch (e) {
      toast.error("Something went wrong.");
    }
  };

  const handleContractorSelect = async (contractor, setFieldValue) => {
    setFieldValue("contact_id", contractor);
    setFieldValue("contract_number", "");
    setFieldValue("contract_value", "");
    setFieldValue("retention_percentage", "");
    setFieldValue("retention_amount", "");
    setFieldValue("file_url", "");
    setFieldValue("documents", []);
    setContractorDetails(contractor);
    await getContractor(contractor?.id, contractor);
  };

  const handleDocumentDelete = (index, setFieldValue) => {
    // Remove the file from the uploadedFiles state
    setUploadedFiles((prevFiles) => {
      const updatedFiles = prevFiles.filter((_, i) => i !== index);

      // Update Formik state
      setFieldValue("documents", updatedFiles);

      return updatedFiles;
    });
  };

  return (
    <Modal
      size="lg"
      show={showAdd}
      onHide={toggleAddModal}
      dialogClassName="modal-50w small-popup review-popup small-review doc-modal"
      aria-labelledby="contained-modal-title-vcenter"
      className="project-section"
      centered
    >
      <Modal.Header className="mb-0" closeButton>
        Add Contract
      </Modal.Header>
      <Modal.Body>
        {loading && <Spin />}
        <Formik
          initialValues={{
            contact_id: contractorDetails ? contractorDetails : "",
            documents: [],
            contract_number: "",
            contract_value: "",
            contract_value_exc: "",
            retention_percentage: "",
            retention_amount: "",
            file_url: "",
          }}
          name="supplierForm"
          enableReinitialize={true}
          onSubmit={(values) => {
            handleSubmit(values);
          }}
          validateOnBlur={true}
          validateOnChange={true}
          validationSchema={AddContractSchema}
        >
          {({
            values,
            setErrors,
            errors,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
          }) => {
            return (
              <>
                {loading && <Spin />}
                <form
                  onSubmit={handleSubmit}
                  noValidate
                  className="entry-section"
                >
                  <div className="form-box" style={{ textAlign: "left" }}>
                    <div className="form-group half-width builder-firm">
                      <label>
                        <span className="required">*</span>Subcontractor
                      </label>
                      <ContractorSelectWithTA
                        regionName={"0"}
                        projectId={projectId}
                        value={values?.contact_id ? values?.contact_id : null}
                        onChange={(contractor) => {
                          handleContractorSelect(contractor, setFieldValue);
                        }}
                        business={true}
                        className="contactname-select"
                        placeholder="Select Subcontractor"
                        isClearable={false}
                      />
                      <span
                        className="text-danger"
                        style={{ color: "red", fontSize: "20px" }}
                      >
                        <ErrorMessage name="contact_id" />
                      </span>
                    </div>
                    <>
                      <div className="form-group half-width">
                        <label>
                          <span className="required">*</span>Contract/Agreement
                          Number
                        </label>
                        <input
                          type="text"
                          name={`contract_number`}
                          value={
                            values?.contract_number
                              ? values?.contract_number
                              : ""
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                          autoComplete="off"
                          onKeyPress={(e) => {
                            e.which === 13 && e.preventDefault();
                          }}
                        />
                        <span
                          className="text-danger"
                          style={{ color: "red", fontSize: "20px" }}
                        >
                          <ErrorMessage name="contract_number" />
                        </span>
                      </div>

                      <div className="form-group half-width">
                        <label>
                          <span className="required">*</span>Contract Value(Inc.
                          GST)
                        </label>
                        <NumberFormat
                          type="text"
                          name={`contract_value`}
                          value={
                            values.contract_value ? values?.contract_value : ""
                          }
                          onChange={handleChange}
                          decimalScale={2}
                          thousandSeparator={true}
                          allowNegative={false}
                          onBlur={(e) => {
                            if (e?.target?.value === "") {
                              setFieldValue(`retention_amount`, "");
                              setFieldValue(`retention_percentage`, "");
                            } else {
                              let contract_value_exc = Number(
                                Number(
                                  Number(e?.target?.value?.replace(/,/g, "")) /
                                    1.1
                                ).toFixed(2)
                              );
                              setFieldValue(
                                `contract_value_exc`,
                                contract_value_exc
                              );
                              if (
                                values?.retention_amount &&
                                values?.retention_percentage
                              ) {
                                let retention_amount =
                                  (Number(e?.target?.value?.replace(/,/g, "")) *
                                    Number(
                                      values?.retention_percentage
                                        ?.toString()
                                        ?.replace("%", "")
                                    )) /
                                  100;
                                setFieldValue(
                                  `retention_amount`,
                                  retention_amount
                                );
                                setFieldValue(
                                  `retention_percentage`,
                                  (100 *
                                    Number(
                                      retention_amount
                                        ?.toString()
                                        ?.replace(/,/g, "")
                                    )) /
                                    Number(e?.target?.value?.replace(/,/g, ""))
                                );
                              }
                            }
                            setFieldValue(`contract_value`, e?.target?.value);
                          }}
                        />
                        <span
                          className="text-danger"
                          style={{ color: "red", fontSize: "20px" }}
                        >
                          <ErrorMessage name="contract_value" />
                        </span>
                      </div>

                      <div className="form-group half-width">
                        <label>
                          <span className="required">*</span>Contract Value(Ex.
                          GST)
                        </label>
                        <NumberFormat
                          type="text"
                          name={`contract_value_exc`}
                          value={
                            values.contract_value_exc
                              ? values?.contract_value_exc
                              : ""
                          }
                          onChange={handleChange}
                          decimalScale={2}
                          thousandSeparator={true}
                          allowNegative={false}
                          disabled
                          onBlur={(e) => {
                            if (e?.target?.value === "") {
                              setFieldValue(`contract_value`, "");
                              setFieldValue(`retention_amount`, "");
                              setFieldValue(`retention_percentage`, "");
                            } else {
                              let contract_value = Number(
                                Number(
                                  Number(e?.target?.value?.replace(/,/g, "")) +
                                    Number(
                                      e?.target?.value?.replace(/,/g, "")
                                    ) *
                                      0.1
                                ).toFixed(2)
                              );
                              setFieldValue(`contract_value`, contract_value);
                              if (
                                values?.retention_amount &&
                                values?.retention_percentage
                              ) {
                                let retention_amount =
                                  (Number(contract_value) *
                                    Number(
                                      values?.retention_percentage
                                        ?.toString()
                                        ?.replace("%", "")
                                    )) /
                                  100;
                                setFieldValue(
                                  `retention_amount`,
                                  retention_amount
                                );
                                setFieldValue(
                                  `retention_percentage`,
                                  (100 *
                                    Number(
                                      retention_amount
                                        ?.toString()
                                        ?.replace(/,/g, "")
                                    )) /
                                    Number(contract_value)
                                );
                              }
                            }
                            setFieldValue(
                              `contract_value_exc`,
                              e?.target?.value
                            );
                          }}
                        />
                        <span
                          className="text-danger"
                          style={{ color: "red", fontSize: "20px" }}
                        >
                          <ErrorMessage name="contract_value_exc" />
                        </span>
                      </div>

                      <div className="form-group half-width">
                        <label>
                          {/* <span className="required">*</span> */}
                          Retention Percentage(%)
                        </label>
                        <NumberFormat
                          type="text"
                          name={`retention_percentage`}
                          value={
                            values.retention_percentage
                              ? values?.retention_percentage
                              : "0%"
                          }
                          suffix="%"
                          onChange={handleChange}
                          onBlur={(e) => {
                            setFieldValue(
                              `retention_percentage`,
                              e?.target?.value
                            );
                            if (e?.target?.value === "") {
                              setFieldValue(`retention_amount`, "");
                            } else {
                              setFieldValue(
                                `retention_amount`,
                                (Number(
                                  values.contract_value
                                    ?.toString()
                                    ?.replace(/,/g, "")
                                ) *
                                  Number(e?.target?.value?.replace("%", ""))) /
                                  100
                              );
                            }
                          }}
                          decimalScale={2}
                          allowNegative={false}
                          isAllowed={(values) => {
                            const { floatValue } = values;
                            return !floatValue || floatValue <= 100;
                          }}
                        />
                        <span
                          className="text-danger"
                          style={{ color: "red", fontSize: "20px" }}
                        >
                          <ErrorMessage name="retention_percentage" />
                        </span>
                      </div>

                      <div className="form-group half-width">
                        <label>
                          {/* <span className="required">*</span> */}
                          Retention Value($)
                        </label>
                        <NumberFormat
                          type="text"
                          name={`retention_amount`}
                          value={
                            values.retention_amount
                              ? values?.retention_amount
                              : "0"
                          }
                          decimalScale={2}
                          onBlur={(e) => {
                            setFieldValue(`retention_amount`, e?.target?.value);
                            if (e?.target?.value === "") {
                              setFieldValue(`retention_percentage`, "");
                            } else {
                              setFieldValue(
                                `retention_percentage`,
                                (100 *
                                  Number(e?.target?.value?.replace(/,/g, ""))) /
                                  Number(
                                    values?.contract_value
                                      ?.toString()
                                      ?.replace(/,/g, "")
                                  )
                              );
                            }
                          }}
                          thousandSeparator={true}
                          allowNegative={false}
                          onChange={handleChange}
                        />
                        <span
                          className="text-danger"
                          style={{ color: "red", fontSize: "20px" }}
                        >
                          <ErrorMessage name="retention_amount" />
                        </span>
                      </div>
                    </>

                    <div className="upload-div document-upload email-form">
                      <label>
                        {/* <span className="required">*</span> */}
                        Document Upload
                      </label>

                      <div className="modal-body">
                        <Form>
                          <div className="upload-file">
                            <FormControl
                              accept={
                                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, image/jpeg, image/jpg, image/x-png, .doc, .docx, .pdf"
                              }
                              type="file"
                              name="documents"
                              className="form-control"
                              id="documents"
                              multiple={false}
                              onChange={(e) => {
                                handleFiles(
                                  Array.from(e.target.files),
                                  setFieldValue,
                                  values?.document_type?.value === 4 ||
                                    values?.document_type?.value === 5
                                    ? true
                                    : false
                                );
                              }}
                            />
                            <span>
                              <p className="ant-upload-drag-icon">
                                <FontAwesomeIcon icon={faUpload} size="3x" />
                              </p>
                              <p className="ant-upload-text">
                                Click or drag file to this area to upload
                              </p>
                            </span>
                          </div>
                          {uploadedFiles && uploadedFiles?.length > 0 && (
                            <div className="file-list-tags">
                              <label>Document List</label>
                              {uploadedFiles?.map((item, index) => {
                                return (
                                  <div key={index} className="tag">
                                    <span title={item.name}>{item.name}</span>
                                    <div className="remove">
                                      <FontAwesomeIcon
                                        icon={faXmarkCircle}
                                        onClick={() => {
                                          handleDocumentDelete(
                                            index,
                                            setFieldValue
                                          );
                                        }}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          <span
                            className="text-danger"
                            style={{ color: "red", fontSize: "20px" }}
                          >
                            <ErrorMessage name="documents" />
                          </span>
                          <div className="or-section-row">
                            <hr />
                            OR
                            <hr />
                          </div>
                          <div className="form-group full-width">
                            <label>
                              {/* <span className="required">*</span> */}
                              File URL
                            </label>
                            <>
                              <input
                                type="text"
                                name={`file_url`}
                                value={values?.file_url ? values?.file_url : ""}
                                placeholder="Enter File URL"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                autoComplete="off"
                                onKeyPress={(e) => {
                                  e.which === 13 && e.preventDefault();
                                }}
                              />
                              <div className="text-danger error-message">
                                <ErrorMessage name="file_url" />
                              </div>
                            </>
                          </div>
                        </Form>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <Button
                      type="submit"
                      className="btn btn-primary"
                      disabled={disableState}
                    >
                      Save
                    </Button>
                    <Button
                      className="btn btn-secondary"
                      data-dismiss="modal"
                      aria-label="Close"
                      onClick={() => {
                        toggleAddModal();
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </>
            );
          }}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default AddContractModal;
